import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Chip,
    IconButton,
    Stack,
    Divider,
    Avatar,
    Skeleton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    AccessTime as ClockIcon,
    CheckCircle as SuccessIcon,
    Error as AlertIcon,
    Group as UsersIcon,
    Inventory as PackageIcon,
    Description as FileIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Build as WrenchIcon,
    PlayArrow as PlayIcon,
    Pause as PauseIcon,
    Person as UserIcon,
    Comment as ChatIcon,
} from '@mui/icons-material';
import serviceTicketService from '../../services/serviceTicketService';
import inventoryService from '../../services/inventoryService';

const ServiceTicketDetail = () => {
    const { ticketId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [ticket, setTicket] = useState(null);
    const [showLogModal, setShowLogModal] = useState(false);
    const [showPartsModal, setShowPartsModal] = useState(false);

    const [logEntry, setLogEntry] = useState({ action_taken: '', remarks: '' });
    const [partsEntry, setPartsEntry] = useState({ item_id: '', quantity_used: 1, notes: '' });
    const [inventory, setInventory] = useState([]);
    const [isClockedIn, setIsClockedIn] = useState(false);

    useEffect(() => {
        if (ticketId) {
            fetchTicketDetails();
            fetchInventory();
        }
    }, [ticketId]);

    const fetchInventory = async () => {
        try {
            const response = await inventoryService.getAllItems();
            if (response.success) setInventory(response.data);
        } catch (error) {
            console.error('Failed to fetch inventory:', error);
        }
    };

    const fetchTicketDetails = async () => {
        try {
            const response = await serviceTicketService.getTicketById(ticketId);
            if (response.success) {
                setTicket(response.data);
                const activeTimeLogs = response.data.timeLogs?.filter(log => !log.clock_out);
                setIsClockedIn(activeTimeLogs && activeTimeLogs.length > 0);
            }
        } catch (error) {
            console.error('Failed to fetch ticket details:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClockToggle = async () => {
        try {
            if (isClockedIn) {
                await serviceTicketService.clockOut(ticketId, 1, 0, '');
            } else {
                await serviceTicketService.clockIn(ticketId, 1);
            }
            setIsClockedIn(!isClockedIn);
            fetchTicketDetails();
        } catch (error) {
            console.error('Failed to toggle clock:', error);
        }
    };

    const handleStatusChange = async (newStatus) => {
        try {
            await serviceTicketService.updateTicketStatus(ticketId, newStatus);
            fetchTicketDetails();
        } catch (error) {
            console.error('Failed to update status:', error);
        }
    };

    const handleLogSubmit = async () => {
        try {
            await serviceTicketService.addLogEntry(ticketId, {
                ...logEntry,
                performed_by: 1 // Default to admin for demo
            });
            setShowLogModal(false);
            setLogEntry({ action_taken: '', remarks: '' });
            fetchTicketDetails();
        } catch (error) {
            console.error('Failed to add log entry:', error);
        }
    };

    const handlePartsSubmit = async () => {
        try {
            await serviceTicketService.requisitionParts(ticketId, {
                ...partsEntry,
                requested_by: 1 // Default to admin for demo
            });
            setShowPartsModal(false);
            setPartsEntry({ item_id: '', quantity_used: 1, notes: '' });
            fetchTicketDetails();
        } catch (error) {
            console.error('Failed to requisition parts:', error);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'OPEN': return 'info';
            case 'IN_PROGRESS': return 'warning';
            case 'RESOLVED': return 'success';
            case 'CLOSED': return 'default';
            default: return 'primary';
        }
    };

    if (loading) {
        return (
            <Box p={4} display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="80vh">
                <Skeleton variant="circular" width={60} height={60} sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary" fontWeight={600}>Synchronizing Ticket Data...</Typography>
            </Box>
        );
    }

    if (!ticket) return null;

    return (
        <Box p={3} className="animate-fade-in">
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <Box display="flex" alignItems="center" gap={2}>
                    <IconButton onClick={() => navigate('/service')} sx={{ bgcolor: 'white', boxShadow: 'var(--shadow-sm)' }}><ArrowBackIcon /></IconButton>
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: -1 }}>Ticket #{ticket.id}</Typography>
                            <Chip label={ticket.status.replace('_', ' ')} color={getStatusColor(ticket.status)} size="small" sx={{ fontWeight: 800, borderRadius: '6px' }} />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>{ticket.device_name} • Serial: {ticket.device_id}</Typography>
                    </Box>
                </Box>
                <Stack direction="row" spacing={2}>
                    <Button variant="contained" color={isClockedIn ? 'error' : 'success'} startIcon={isClockedIn ? <PauseIcon /> : <PlayIcon />} onClick={handleClockToggle} sx={{ borderRadius: '12px', fontWeight: 800 }}>
                        {isClockedIn ? 'Clock Out' : 'Clock In'}
                    </Button>
                    <Button variant="contained" className="gradient-primary" startIcon={<AddIcon />} onClick={() => setShowLogModal(true)}>Append Log</Button>
                </Stack>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} lg={8}>
                    <Stack spacing={3}>
                        <Card sx={{ borderRadius: '24px' }}>
                            <Box p={3} sx={{ borderBottom: 1, borderColor: 'divider' }}><Typography variant="h6" fontWeight={800}>Diagnostics & Fault Data</Typography></Box>
                            <CardContent>
                                <Grid container spacing={3}>
                                    <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase' }}>Issue Domain</Typography><Typography variant="body1" fontWeight={700}>{ticket.issue_type?.replace('_', ' ')}</Typography></Grid>
                                    <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase' }}>Urgency</Typography><Typography variant="body1" fontWeight={700} color="error.main">{ticket.priority}</Typography></Grid>
                                    <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase' }}>Timestamp</Typography><Typography variant="body2" fontWeight={700}>{new Date(ticket.opened_at).toLocaleString()}</Typography></Grid>
                                    <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase' }}>Labor Burn</Typography><Typography variant="body1" fontWeight={700}>{ticket.total_labor_hours || 0} hrs</Typography></Grid>
                                    <Grid item xs={12}><Divider sx={{ my: 1 }} /><Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase' }}>Symptom Description</Typography><Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontWeight: 500 }}>{ticket.description || 'No detailed description provided.'}</Typography></Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        <Card sx={{ borderRadius: '24px' }}>
                            <Box p={3} sx={{ borderBottom: 1, borderColor: 'divider' }}><Typography variant="h6" fontWeight={800}>Service Narrative</Typography></Box>
                            <CardContent>
                                <Box sx={{ position: 'relative', ml: 2 }}>
                                    <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 2, bgcolor: 'grey.100' }} />
                                    <Stack spacing={4}>
                                        {ticket.logs?.length > 0 ? ticket.logs.map((log, i) => (
                                            <Box key={i} sx={{ position: 'relative', pl: 4 }}>
                                                <Box sx={{ position: 'absolute', left: -5, top: 0, width: 12, height: 12, borderRadius: '50%', bgcolor: 'primary.main', border: '3px solid white', boxShadow: '0 0 0 2px var(--primary-light)' }} />
                                                <Typography variant="caption" color="text.secondary" fontWeight={800}>{new Date(log.logged_at).toLocaleString()}</Typography>
                                                <Typography variant="subtitle1" fontWeight={800} sx={{ mt: 0.5 }}>{log.action_taken}</Typography>
                                                <Typography variant="body2" color="text.secondary" mt={0.5}>{log.remarks}</Typography>
                                                <Box display="flex" alignItems="center" gap={1} mt={1.5} p={1} bgcolor="grey.50" borderRadius="8px" sx={{ width: 'fit-content' }}>
                                                    <Avatar sx={{ width: 20, height: 20, fontSize: '10px', bgcolor: 'primary.main' }}>{log.performed_by_name?.charAt(0)}</Avatar>
                                                    <Typography variant="caption" fontWeight={700}>{log.performed_by_name}</Typography>
                                                </Box>
                                            </Box>
                                        )) : (
                                            <Typography variant="body2" color="text.secondary" textAlign="center" py={4} sx={{ fontStyle: 'italic' }}>Historical events will accumulate here as technician logs are published.</Typography>
                                        )}
                                    </Stack>
                                </Box>
                            </CardContent>
                        </Card>
                    </Stack>
                </Grid>

                <Grid item xs={12} lg={4}>
                    <Stack spacing={3}>
                        <Card sx={{ borderRadius: '24px' }}>
                            <Box p={2.5} sx={{ borderBottom: 1, borderColor: 'divider' }}><Typography variant="subtitle2" fontWeight={800}>TECHNICAL TEAM</Typography></Box>
                            <CardContent>
                                {ticket.assigned_technician_name ? (
                                    <Box display="flex" alignItems="center" gap={2} p={2} bgcolor="grey.50" borderRadius="16px">
                                        <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.100', color: 'primary.main' }}><UserIcon /></Avatar>
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight={800}>{ticket.assigned_technician_name}</Typography>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Primary Field Engineer</Typography>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>No technician dispatched.</Typography>
                                )}
                            </CardContent>
                        </Card>

                        <Card sx={{ borderRadius: '24px' }}>
                            <Box p={2.5} sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2" fontWeight={800}>REQUISITIONED PARTS</Typography>
                                <Button size="small" sx={{ fontWeight: 800 }} onClick={() => setShowPartsModal(true)}>Order</Button>
                            </Box>
                            <CardContent>
                                {ticket.parts?.length > 0 ? (
                                    <Stack spacing={2}>
                                        {ticket.parts.map((part, i) => (
                                            <Box key={i} display="flex" justifyContent="space-between" alignItems="center" p={1.5} border="1px solid" borderColor="divider" borderRadius="12px">
                                                <Box><Typography variant="body2" fontWeight={800}>{part.item_name}</Typography><Typography variant="caption" color="text.secondary">Quantity: {part.quantity_used}</Typography></Box>
                                                <Chip label={part.status} size="small" variant="outlined" sx={{ fontSize: '9px', fontWeight: 900, borderRadius: '4px' }} />
                                            </Box>
                                        ))}
                                    </Stack>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>No bill of materials recorded.</Typography>
                                )}
                            </CardContent>
                        </Card>

                        <Card sx={{ borderRadius: '24px' }}>
                            <Box p={2.5} sx={{ borderBottom: 1, borderColor: 'divider' }}><Typography variant="subtitle2" fontWeight={800}>WORKFLOW CONTROL</Typography></Box>
                            <CardContent>
                                <Stack spacing={1.5}>
                                    <Button fullWidth variant="outlined" color="warning" sx={{ borderRadius: '12px', fontWeight: 800 }} onClick={() => handleStatusChange('IN_PROGRESS')}>Mark In Progress</Button>
                                    <Button fullWidth variant="outlined" color="success" sx={{ borderRadius: '12px', fontWeight: 800 }} onClick={() => handleStatusChange('RESOLVED')}>Certify Resolution</Button>
                                    <Button fullWidth variant="outlined" color="inherit" sx={{ borderRadius: '12px', fontWeight: 800 }} onClick={() => handleStatusChange('CLOSED')}>Finalize & Close</Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Stack>
                </Grid>
            </Grid>

            {/* Modals */}
            <Dialog open={showLogModal} onClose={() => setShowLogModal(false)} PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>Append Service Entry</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField fullWidth label="Action Decription" placeholder="e.g. Calibrated pressure sensor" value={logEntry.action_taken} onChange={(e) => setLogEntry({ ...logEntry, action_taken: e.target.value })} />
                        <TextField fullWidth multiline rows={3} label="Technical Remarks" placeholder="Additional observations..." value={logEntry.remarks} onChange={(e) => setLogEntry({ ...logEntry, remarks: e.target.value })} />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setShowLogModal(false)} sx={{ fontWeight: 800 }}>Discard</Button>
                    <Button variant="contained" className="gradient-primary" sx={{ borderRadius: '12px', px: 4 }} onClick={handleLogSubmit}>Commit Log</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={showPartsModal} onClose={() => setShowPartsModal(false)} PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>Requisition Components</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField select fullWidth label="Inventory Item" value={partsEntry.item_id} onChange={(e) => setPartsEntry({ ...partsEntry, item_id: e.target.value })}>
                            <MenuItem value="">Select Part...</MenuItem>
                            {inventory.map(item => <MenuItem key={item.id} value={item.id} disabled={item.current_stock <= 0}>{item.item_name} ({item.current_stock} avail)</MenuItem>)}
                        </TextField>
                        <TextField fullWidth type="number" label="Requested Quantity" value={partsEntry.quantity_used} onChange={(e) => setPartsEntry({ ...partsEntry, quantity_used: e.target.value })} />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setShowPartsModal(false)} sx={{ fontWeight: 800 }}>Cancel</Button>
                    <Button variant="contained" className="gradient-primary" sx={{ borderRadius: '12px', px: 4 }} onClick={handlePartsSubmit}>Place Order</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ServiceTicketDetail;
